/**
 * RouteDetectionService - Context-aware route detection for contextual call handling
 * Provides lightweight route detection capabilities for incoming call navigation logic
 */

import { EventEmitter } from './EventEmitter'

export interface RouteInfo {
  currentPath: string
  isOnChatPage: boolean
  needsNavigation: boolean
}

export class RouteDetectionService extends EventEmitter {
  private currentPath: string = '/'
  private chatPath: string = '/chat'

  constructor() {
    super()
    this.initializeRouteTracking()
  }

  /**
   * Initialize route tracking using browser location APIs
   */
  private initializeRouteTracking(): void {
    // Initial path detection
    this.currentPath = window.location.pathname
    console.log('🧭 RouteDetectionService initialized - Current path:', this.currentPath)

    // Listen for route changes (works with React Router)
    // Use both popstate and custom router events
    window.addEventListener('popstate', this.handleRouteChange.bind(this))

    // Also track pushState/replaceState which React Router uses
    this.interceptHistoryMethods()
  }

  /**
   * Intercept browser history methods to detect programmatic navigation
   */
  private interceptHistoryMethods(): void {
    const originalPushState = history.pushState
    const originalReplaceState = history.replaceState

    history.pushState = (...args) => {
      originalPushState.apply(history, args)
      this.handleRouteChange()
    }

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args)
      this.handleRouteChange()
    }
  }

  /**
   * Handle route changes and emit events
   */
  private handleRouteChange(): void {
    const newPath = window.location.pathname
    const oldPath = this.currentPath

    if (newPath !== oldPath) {
      this.currentPath = newPath
      console.log(`🧭 Route changed from ${oldPath} to ${newPath}`)

      const routeInfo = this.getCurrentRouteInfo()
      this.emit('routeChanged', { oldPath, newPath, routeInfo })
    }
  }

  /**
   * Get current route information
   */
  getCurrentRouteInfo(): RouteInfo {
    const isOnChatPage = this.currentPath === this.chatPath
    const needsNavigation = !isOnChatPage

    return {
      currentPath: this.currentPath,
      isOnChatPage,
      needsNavigation
    }
  }

  /**
   * Check if user is currently on the chat page
   */
  isOnChatPage(): boolean {
    return this.currentPath === this.chatPath
  }

  /**
   * Check if navigation to chat page is needed for optimal call experience
   */
  shouldNavigateToChat(): boolean {
    return !this.isOnChatPage()
  }

  /**
   * Get the target path for navigation during incoming calls
   */
  getCallNavigationTarget(): string {
    return this.chatPath
  }

  /**
   * Update the chat path if it changes (for flexibility)
   */
  setChatPath(path: string): void {
    this.chatPath = path
    console.log('🧭 Chat path updated to:', path)
  }

  /**
   * Manual path update (for testing or special cases)
   */
  updateCurrentPath(path: string): void {
    const oldPath = this.currentPath
    this.currentPath = path
    console.log(`🧭 Manual path update from ${oldPath} to ${path}`)

    const routeInfo = this.getCurrentRouteInfo()
    this.emit('routeChanged', { oldPath, newPath: path, routeInfo })
  }

  /**
   * Add route change listener
   */
  onRouteChange(listener: (data: { oldPath: string; newPath: string; routeInfo: RouteInfo }) => void): () => void {
    const wrappedListener = (eventData: any) => {
      listener(eventData)
    }
    this.on('routeChanged', wrappedListener)
    return () => this.off('routeChanged', wrappedListener)
  }

  /**
   * Clean up event listeners
   */
  cleanup(): void {
    window.removeEventListener('popstate', this.handleRouteChange.bind(this))
    super.destroy()
    console.log('🧭 RouteDetectionService cleaned up')
  }

  /**
   * Destroy service and clean up all resources
   */
  destroy(): void {
    this.cleanup()
    console.log('🧭 RouteDetectionService destroyed')
  }
}

// Export singleton instance for global use
export const routeDetectionService = new RouteDetectionService()